"use client";

import {
  ActivityIcon,
  BugIcon,
  CalendarClockIcon,
  CheckIcon,
  CheckCircle2Icon,
  FileTextIcon,
  PauseIcon,
  PlayIcon,
  RefreshCwIcon,
  ShieldCheckIcon,
  SparklesIcon,
} from "lucide-react";
import type {
  CmmdForgeAutomationPreset,
  CmmdForgeAutomationRule,
  CmmdForgeAutomationRun,
} from "@cmmd-forge/contracts";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { ProjectAutomationRunsSection } from "./ProjectAutomationRunsSection";
import { ProjectAutomationStatusDot } from "./ProjectAutomationStatusDot";

export interface ProjectAutomationsPanelProps {
  readonly cmmdLinked: boolean;
  readonly loading: boolean;
  readonly creatingPresetKey: string | null;
  readonly updatingRuleId: number | null;
  readonly presets: readonly CmmdForgeAutomationPreset[];
  readonly rules: readonly CmmdForgeAutomationRule[];
  readonly runs: readonly CmmdForgeAutomationRun[];
  readonly error: string | null;
  readonly onCreatePreset: (presetKey: string) => void;
  readonly onToggleRule: (rule: CmmdForgeAutomationRule) => void;
  readonly onSelectRun?: (run: CmmdForgeAutomationRun) => void;
  readonly onRefresh: () => void;
  readonly onLinkCmmdProject: () => void;
  readonly emptyTitle?: string;
  readonly emptyDescription?: string;
  readonly emptyActionLabel?: string;
  /** When true, omit the inline Refresh control (hosted in project tabs trailing). */
  readonly hideChromeRefresh?: boolean;
}

function presetIcon(key: string) {
  if (key.includes("manual")) return PlayIcon;
  if (key.includes("bug")) return BugIcon;
  if (key.includes("weekly")) return FileTextIcon;
  return CalendarClockIcon;
}

function triggerLabel(rule: CmmdForgeAutomationRule): string {
  if (rule.triggerType === "manual") return "Manual";
  if (rule.triggerType === "work_item_created") return "On new bug work item";
  return rule.scheduleDescription ?? rule.cronExpression ?? "Scheduled";
}

function presetTriggerLabel(preset: CmmdForgeAutomationPreset): string {
  if (preset.triggerType === "manual") return "Run from a work item";
  if (preset.triggerType === "work_item_created") return "Triggered by work item";
  return preset.scheduleDescription ?? preset.cronExpression ?? "Scheduled";
}

function AutomationsSkeleton() {
  return (
    <div className="space-y-8">
      <section>
        <Skeleton className="h-5 w-32 rounded-full" />
        <div className="mt-3 grid gap-3 md:grid-cols-3">
          {["preset-1", "preset-2", "preset-3"].map((id) => (
            <Skeleton key={id} className="h-32 w-full rounded-md" />
          ))}
        </div>
      </section>
      <section>
        <Skeleton className="h-5 w-28 rounded-full" />
        <div className="mt-3 divide-y divide-border/55 border-y border-border/60">
          {["rule-1", "rule-2"].map((id) => (
            <div key={id} className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 py-4">
              <div>
                <Skeleton className="h-4 w-56 rounded-full" />
                <Skeleton className="mt-2 h-3 w-40 rounded-full" />
              </div>
              <Skeleton className="h-8 w-20 rounded-md" />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

interface EmptyLinkedStateProps {
  readonly onLinkCmmdProject: () => void;
  readonly emptyTitle: string | undefined;
  readonly emptyDescription: string | undefined;
  readonly emptyActionLabel: string | undefined;
}

function EmptyLinkedState({
  onLinkCmmdProject,
  emptyTitle,
  emptyDescription,
  emptyActionLabel,
}: EmptyLinkedStateProps) {
  return (
    <section className="flex min-h-[360px] items-center justify-center">
      <div className="max-w-md text-center">
        <div className="mx-auto flex size-11 items-center justify-center rounded-md border border-border/60 bg-card/55 text-muted-foreground">
          <ShieldCheckIcon className="size-5" />
        </div>
        <h2 className="mt-4 text-lg font-semibold text-foreground">
          {emptyTitle ?? "Link CMMD to enable automations"}
        </h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          {emptyDescription ??
            "Automations run from CMMD project context and launch Forge cloud work in the linked execution project."}
        </p>
        <Button className="mt-4" size="sm" onClick={onLinkCmmdProject}>
          {emptyActionLabel ?? "Link CMMD project"}
        </Button>
      </div>
    </section>
  );
}

function PresetsSection({
  presets,
  rules,
  creatingPresetKey,
  onCreatePreset,
}: Pick<
  ProjectAutomationsPanelProps,
  "presets" | "rules" | "creatingPresetKey" | "onCreatePreset"
>) {
  const installedPresetKeys = new Set(
    rules.map((rule) => rule.presetKey).filter((key): key is string => key !== null),
  );
  return (
    <section>
      <div className="mb-3 flex min-w-0 items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Presets</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Add a governed workflow to this project, then tune its prompt.
          </p>
        </div>
      </div>
      <div className="grid gap-3 lg:grid-cols-3">
        {presets.map((preset) => {
          const Icon = presetIcon(preset.key);
          const creating = creatingPresetKey === preset.key;
          const installed = installedPresetKeys.has(preset.key);
          return (
            <button
              key={preset.key}
              type="button"
              onClick={() => onCreatePreset(preset.key)}
              disabled={creating || installed}
              className="group min-h-36 rounded-md border border-border/60 bg-card/35 p-4 text-left transition-colors hover:border-border hover:bg-card/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-wait disabled:opacity-70"
            >
              <div className="flex min-w-0 items-center gap-2">
                <Icon className="size-4 shrink-0 text-hue-sky-foreground" />
                <h3 className="truncate text-sm font-semibold text-foreground">{preset.name}</h3>
              </div>
              <p className="mt-3 line-clamp-3 text-sm leading-6 text-muted-foreground">
                {preset.description}
              </p>
              <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                {preset.triggerType === "schedule" ? (
                  <CalendarClockIcon className="size-3.5" />
                ) : preset.triggerType === "manual" ? (
                  <PlayIcon className="size-3.5" />
                ) : (
                  <ActivityIcon className="size-3.5" />
                )}
                <span>{presetTriggerLabel(preset)}</span>
              </div>
              <div className="mt-3 flex items-center gap-1.5 text-xs font-medium text-foreground/75">
                {installed ? <CheckIcon className="size-3.5" /> : null}
                <span>{installed ? "Added" : creating ? "Adding…" : "Add to project"}</span>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function RulesSection({
  rules,
  updatingRuleId,
  onToggleRule,
}: Pick<ProjectAutomationsPanelProps, "rules" | "updatingRuleId" | "onToggleRule">) {
  return (
    <section>
      <h2 className="mb-3 text-sm font-semibold text-foreground">Current</h2>
      {rules.length === 0 ? (
        <div className="border-y border-border/60 py-5 text-sm text-muted-foreground">
          No automations configured yet.
        </div>
      ) : (
        <div className="divide-y divide-border/55 border-y border-border/60">
          {rules.map((rule) => {
            const active = rule.status === "active";
            const busy = updatingRuleId === rule.id;
            return (
              <div key={rule.id} className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] gap-4 py-4">
                <div className="min-w-0">
                  <div className="flex min-w-0 items-center gap-3">
                    <ProjectAutomationStatusDot tone={active ? "ready" : "neutral"} />
                    <div className="min-w-0 truncate text-sm font-medium text-foreground">
                      {rule.name}
                    </div>
                  </div>
                  <div className="mt-1 ml-5 flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
                    <span className="truncate">{triggerLabel(rule)}</span>
                    {rule.requirePrReview ? (
                      <>
                        <span className="size-1 rounded-full bg-muted-foreground/35" />
                        <span>No auto-merge</span>
                      </>
                    ) : null}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => onToggleRule(rule)}
                >
                  {active ? <PauseIcon className="size-3.5" /> : <PlayIcon className="size-3.5" />}
                  {active ? "Pause" : "Resume"}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export function ProjectAutomationsPanel(props: ProjectAutomationsPanelProps) {
  if (!props.cmmdLinked) {
    return (
      <EmptyLinkedState
        onLinkCmmdProject={props.onLinkCmmdProject}
        emptyTitle={props.emptyTitle}
        emptyDescription={props.emptyDescription}
        emptyActionLabel={props.emptyActionLabel}
      />
    );
  }
  if (props.loading) return <AutomationsSkeleton />;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8 px-4 py-6 sm:px-6">
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          {/* h2, not h1: this is a panel inside the project surface. The page's
              identity is the project, and the bar carries it — this only names
              the section you are looking at within it. */}
          <h2 className="truncate text-xl font-semibold tracking-tight text-foreground">
            Automations
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Project triggers, schedules, and governed team workflows.
          </p>
        </div>
        {props.hideChromeRefresh ? null : (
          <Button size="sm" variant="outline" onClick={props.onRefresh}>
            <RefreshCwIcon className="size-3.5" />
            Refresh
          </Button>
        )}
      </div>

      {props.error ? (
        <div className="rounded-md border border-destructive-border bg-destructive-surface px-3 py-2 text-sm text-destructive">
          {props.error}
        </div>
      ) : null}

      <PresetsSection
        presets={props.presets}
        rules={props.rules}
        creatingPresetKey={props.creatingPresetKey}
        onCreatePreset={props.onCreatePreset}
      />
      <RulesSection
        rules={props.rules}
        updatingRuleId={props.updatingRuleId}
        onToggleRule={props.onToggleRule}
      />
      <ProjectAutomationRunsSection runs={props.runs} onSelectRun={props.onSelectRun} />
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <CheckCircle2Icon className="size-3.5 text-success-foreground" />
        <span>Automations create PRs for review. They do not auto-merge.</span>
        <SparklesIcon className="size-3.5" />
      </div>
    </div>
  );
}
